/**
 * Scan documents to prefill property setup (PRD 2026-09-23).
 *
 * A reading (the extraction service's per-field envelopes) becomes SUGGESTIONS for the wizard's
 * own fields. Nothing here writes anything: the wizard shows each suggestion in its field, the
 * admin accepts or dismisses it, and only accepted or typed values are saved. Two documents that
 * disagree on a field are a CONFLICT the admin must settle; no default is picked.
 */

export type WizardField =
  | 'name' | 'address_line1' | 'city' | 'state' | 'postal_code' | 'prop_type' | 'units'
  | 'owner_name' | 'legal_description' | 'account_number'
  | 'legal_owner_name' | 'purchase_price' | 'purchase_date' | 'reported_noi' | 'reported_noi_year';

export interface Suggestion {
  field: WizardField;
  value: string;
  documentId: string;
  documentName: string;
  /** 0..1 from the reader; null when the source is a deterministic read (a workbook). */
  confidence: number | null;
}

interface FieldEnvelope { value?: unknown; confidence?: number }
type Fields = Record<string, FieldEnvelope | undefined>;

/** Below this the field shows a caution marker rather than being hidden (PRD 2c). */
export const LOW_CONFIDENCE = 0.7;

/** Which extraction fields feed which wizard fields, per document type (PRD 2b). */
const FIELD_MAP: Record<string, Array<[string, WizardField]>> = {
  'Notice of Value': [
    ['property_name', 'name'], ['address_line1', 'address_line1'], ['parcel_city', 'city'], ['parcel_state', 'state'],
    ['parcel_postal_code', 'postal_code'], ['property_type', 'prop_type'], ['owner_name', 'owner_name'],
    ['legal_description', 'legal_description'], ['account_number', 'account_number'],
  ],
  'Tax Bill': [['owner_name', 'owner_name'], ['address_line1', 'address_line1'], ['account_number', 'account_number']],
  'Deed': [
    ['legal_owner_name', 'legal_owner_name'], ['legal_description', 'legal_description'], ['purchase_price', 'purchase_price'],
    ['purchase_date', 'purchase_date'], ['address_line1', 'address_line1'], ['parcel_city', 'city'], ['parcel_state', 'state'],
    ['parcel_postal_code', 'postal_code'], ['account_number', 'account_number'],
  ],
  'Board Order': [['account_number', 'account_number'], ['owner_name', 'owner_name']],
};

function asText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : '';
  return String(v).trim();
}

/** Normalize per PRD 2c: money strips symbols and separators; dates stay ISO; account numbers keep exact formatting. */
function normalize(field: WizardField, raw: string): string {
  if (field === 'purchase_price' || field === 'reported_noi') return raw.replace(/[^0-9.-]/g, '');
  if (field === 'state') return raw.toUpperCase().slice(0, 2);
  if (field === 'units' || field === 'reported_noi_year') return raw.replace(/[^0-9]/g, '');
  return raw;
}

/** Suggestions from one AI reading. Unknown document types yield none (they attach as Other). */
export function suggestionsFromReading(docType: string | null | undefined, fields: Fields | undefined, doc: { id: string; name: string }): Suggestion[] {
  const map = docType ? FIELD_MAP[docType] : undefined;
  if (!map || !fields) return [];
  const out: Suggestion[] = [];
  for (const [source, field] of map) {
    const env = fields[source];
    const text = normalize(field, asText(env?.value));
    if (!text) continue;
    const confidence = typeof env?.confidence === 'number' ? env.confidence : null;
    out.push({ field, value: text, documentId: doc.id, documentName: doc.name, confidence });
  }
  return out;
}

/** Suggestions from an operating statement read (deterministic, no confidence). */
export function suggestionsFromOperatingStatement(
  extracted: { units?: number | null; reported_noi?: number | null; reported_noi_year?: number | null } | null | undefined,
  doc: { id: string; name: string },
): Suggestion[] {
  if (!extracted) return [];
  const out: Suggestion[] = [];
  const push = (field: WizardField, v: unknown) => { const t = normalize(field, asText(v)); if (t) out.push({ field, value: t, documentId: doc.id, documentName: doc.name, confidence: null }); };
  push('units', extracted.units);
  push('reported_noi', extracted.reported_noi);
  push('reported_noi_year', extracted.reported_noi_year);
  return out;
}

export interface FieldChoice { value: string; sources: Suggestion[] }
export interface FieldSuggestion { field: WizardField; choices: FieldChoice[]; conflict: boolean }

/** Group suggestions per field; two distinct values for one field are a conflict (PRD 2a.5). */
export function groupSuggestions(list: Suggestion[]): Map<WizardField, FieldSuggestion> {
  const out = new Map<WizardField, FieldSuggestion>();
  for (const s of list) {
    const entry = out.get(s.field) ?? { field: s.field, choices: [], conflict: false };
    const key = s.value.trim().toLowerCase();
    const choice = entry.choices.find((c) => c.value.trim().toLowerCase() === key);
    if (choice) choice.sources.push(s); else entry.choices.push({ value: s.value, sources: [s] });
    entry.conflict = entry.choices.length > 1;
    out.set(s.field, entry);
  }
  return out;
}

/** The best single value for a field with no conflict: the choice, with its highest-confidence source first. */
export function bestSource(choice: FieldChoice): Suggestion {
  return [...choice.sources].sort((a, b) => (b.confidence ?? 1) - (a.confidence ?? 1))[0];
}
